import React, { useState, useEffect } from 'react'
// import PropTypes from 'prop-types';
import { fetchFromBilling } from './billingAccess'
import { path } from 'ramda'
import Switch from 'react-switch'
import { Loader } from '../../components'
import bindAllActions from '../../common/bindAllActions'
import { isCellularDevice, hasCellularSubscription, displayPriceForSubscription, subIsIap, subIsIos, subStatusDisplay, awnPlusAnnualMonthlySwitchAvail, subIsIosDowngradedAnnual, USE_NEW_CELL_PLAN, subIsPlus, displayCancelPlusOnIosMessage, getExpiredCellularSubscription, subIsTeams, subIsActive, getSubscriptionDisplayName, getSubscriptionConfig, hasIapPlatformMismatch } from '.'
import { isAndroidApp, isApp, isIosApp } from '../../common/ambient'

// Auto-renew toggle component
function AutoRenewToggle ({ sub, user, device, userActions, index, subsUpdating, setSubsUpdating }) {
  // Loading state
  if (subsUpdating.includes(index)) {
    return <Loader />
  }
  
  // Placeholder for other updating subs or free status
  if (subsUpdating.length > 0 || sub.status === 'free') {
    return <div className='placeholder' />
  }
  
  // IAP subscriptions on wrong platform or web - show manage links
  if (subIsIap(sub) && (
    (!isApp()) || // Not in any app
    (subIsIos(sub) && !isIosApp()) || // Apple sub but not on iOS
    (!subIsIos(sub) && !isAndroidApp()) // Google sub but not on Android
  )) {
    if (subIsIos(sub)) {
      return <a href='https://apps.apple.com/account/subscriptions' target='_blank'>Manage in App Store</a>
    } else {
      return <a href={`https://play.google.com/store/account/subscriptions?sku=${sub.googleProductId}&package=net.ambientweather.dashboard`} target='_blank'>Manage in Play Store</a>
    }
  }
  
  // Gift code subscriptions without auto-renew change allowed
  if (subIsPlus(sub) && sub.promoCode && !sub.autoRenewChangeIsAllowed) {
    return 'Free'
  }
  
  // Station deleted
  const devices = path(['devices'], device) || []
  if (sub.macAddress && !devices.find(d => d.macAddress === sub.macAddress)) {
    return 'Station deleted'
  }
  
  // Default: show toggle switch
  return (
    <Switch
      onChange={async () => {
        if (subIsIap(sub)) {
          if (isIosApp()) {
            window.webkit.messageHandlers.showManageSubscriptions.postMessage({})
          } else if (window.ANDROID) {
            window.ANDROID.showManageSubscriptions(sub.googleProductId, path(['info', '_id'], user))
          }
        } else {
          setSubsUpdating([...subsUpdating, index])
          const cancel_at_period_end = !sub.cancelAtPeriodEnd
          const res = await fetchFromBilling(`/subscriptions?userId=${path(['info', '_id'], user)}&macAddress=${sub.macAddress}&productName=${sub.productName}`, {
            method: 'PUT',
            body: JSON.stringify({
              cancel_at_period_end
            }),
            headers: {
              'Content-Type': 'application/json'
            }
          })
          const newUser = Object.assign({}, user.info)
          newUser.payments.subscriptions[index].cancelAtPeriodEnd = cancel_at_period_end
          await userActions.refreshUser(newUser)
          setSubsUpdating(subsUpdating.filter(j => j !== index))
        }
      }}
      checked={!sub.cancelAtPeriodEnd}
    />
  )
}

// Upgrade/Downgrade link component for AWN Plus subscriptions
function UpgradeDowngradeLink ({ sub, user, userActions }) {
  if (!subIsPlus(sub)) {
    return null
  }
  if (!subIsActive(sub)) {
    return null
  }
  if (sub.willDowngradeAtPeriodEnd) {
    return null
  }

  // Don't show upgrade/downgrade link for IAP subscriptions when platform mismatch
  if (hasIapPlatformMismatch(sub)) {
    return null
  }

  const isTeams = subIsTeams(sub)
  const handleClick = () => {
    const isMonthly = /monthly/.test(sub.productName)
    if (isTeams) {
      // Downgrade from teams to individual
      userActions.doModal({
        type: 'plus-subscription',
        data: {
          tier: 'plus',  // Use 'plus' instead of 'individual' to match PlusSubscriptionModal
          forceChange: true,
          initialAnnual: !isMonthly  // Pass whether they're currently annual
        }
      })
    } else {
      // Upgrade from individual to teams
      userActions.doModal({
        type: 'plus-subscription',
        data: {
          tier: 'teams',
          forceChange: true,
          initialAnnual: !isMonthly  // Pass whether they're currently annual
        }
      })
    }
  }

  return (
    <>
      <br/>
      <a
        className={isTeams ? 'downgrade-link' : 'upgrade-link'}
        onClick={handleClick}
      >
        {isTeams ? 'Downgrade' : 'Upgrade'}
      </a>
    </>
  )
}

// Should display a row for each case:
// * An active subscription, regardless of stationType.  This supports deleted stations that could be re-added.
// * StationType == KestrelMet6000.  This supports adding new subscription to a lapsed station.

function DisplaySubscription ({ user, device, userActions }) {
  const [subsUpdating, setSubsUpdating] = useState([])
  const userSubs = path(['info', 'payments', 'subscriptions'], user) || []
  const devices = path(['devices'], device) || []

  let atLeastOneRow = false
  if (userSubs.length > 0) atLeastOneRow = true
  const noSubscriptions = (
    <tr>
      <td colspan='7'>
        You currently have no subscriptions.
      </td>
    </tr>
  )
  // init
  useEffect(() => {
    userActions.refreshUser()
  }, [userActions])

  const hasMultiplePlusSubs = userSubs.filter(subIsPlus).length > 1

  return (
    <div className='payment-display-subscription'>
      <div className='aw-fields'>
        <table>
          <thead>
            <th>Applies to</th>
            <th>Product</th>
            <th>Billing Period</th>
            <th>Status</th>
            <th>Renews/Expires</th>
            <th>Renewal Price</th>
            <th>Auto-renew</th>
          </thead>
          <tbody>
            {devices.map((dev, i) => {
              const mac = path(['macAddress'], dev)
              const cellDeviceNeedsSubscription = (isCellularDevice(dev) && !hasCellularSubscription(user, mac))
              // const cellDeviceNeedsSubscription = (isCellularDevice(dev) && true)
              if (!cellDeviceNeedsSubscription) return null
              atLeastOneRow = true
              const currentDevice = dev // for naming consistency in CellPlanModal
              const expiredSubscription = getExpiredCellularSubscription(user, mac)

              return (
                <tr key={i}>
                  <td>{mac}</td>
                  <td>KestrelMet Standard 1 Year Subscription</td>
                  <td>Annual</td>
                  <td>{expiredSubscription ? 'Expired' : '--'}</td>
                  <td>{expiredSubscription ? `${moment(expiredSubscription.timestamp).format('MMM D, YYYY')}` : '--'}</td>
                  <td>${USE_NEW_CELL_PLAN ? '9' : '4'}9.99</td>
                  <td>
                    <button onClick={() => userActions.doModal({ type: 'cell-plan', data: { currentDevice } })} className='btn btn-primary btn-lg'>Add Plan</button>
                  </td>
                </tr>
              )
            })}

            {userSubs.map((sub, i) => {
              if (hasMultiplePlusSubs && subIsPlus(sub) && subIsIap(sub)) {
                return null
              }
              if (/awn_sms/.test(sub.productName)) {
                return null
              }
              return (
                <tr key={i}>
                  <td>{sub.macAddress || 'Account'}</td>
                  <td>
                    {getSubscriptionDisplayName(sub)}
                    <UpgradeDowngradeLink sub={sub} user={user} userActions={userActions} />
                    {subIsPlus(sub) && displayCancelPlusOnIosMessage(user) && (
                      <a href='https://apps.apple.com/account/subscriptions' target='_blank' rel='noreferrer' className='cancel'>Cancel your Apple subscription</a>
                    )}
                  </td>
                  <td>
                    {/monthly/.test(sub.productName) ? 'Monthly' : 'Annual'}<br/>
                    {awnPlusAnnualMonthlySwitchAvail(user, sub) && (
                      <a
                        className='change-plan'
                        onClick={() => userActions.doModal({ type: 'plus-subscription' })}
                      >
                        Change plan
                      </a>
                    )}
                    {/awn-camera/.test(sub.productName) && (
                      <a
                        className='change-plan'
                        onClick={() => userActions.doModal({ type: 'enhanced-cam', data: { currentDevice: device.devices.find(d => d.macAddress === sub.macAddress) } })}
                      >
                        Change plan
                      </a>
                    )}
                  </td>
                  <td>{subStatusDisplay(sub)}</td>
                  <td>{moment(sub.currentPeriodEnd * 1000).format('MMM, D, YYYY')}</td>
                  <td>{displayPriceForSubscription(sub)}</td>
                  <td className='switch'>
                    <AutoRenewToggle 
                      sub={sub} 
                      user={user} 
                      device={device} 
                      userActions={userActions} 
                      index={i} 
                      subsUpdating={subsUpdating} 
                      setSubsUpdating={setSubsUpdating} 
                    />
                  </td>
                </tr>
              )
            })}
            {atLeastOneRow ? '' : noSubscriptions}
          </tbody>
        </table>
      </div>
    </div>
  )
}
export default bindAllActions(DisplaySubscription)

DisplaySubscription.propTypes = {}
DisplaySubscription.defaultProps = {}

DisplaySubscription.displayName = 'DisplaySubscription'
